// The path of a piece that a side carries from one square to the next: it
// comes up, hangs for a moment, travels, and is set down. `planCarry` draws
// the values that differ from one carry to the next. `carryPose` gives the
// place, the turn, and the size at each moment. Pure. Times are in beat
// milliseconds, before `BEAT_PACE`.

import {
  BOARD_SIZE,
  CARRY_BOW_PX,
  CARRY_DROP_PORTION,
  CARRY_LAND_MS,
  CARRY_LEAN_DEGREES,
  CARRY_LIFT_MS,
  CARRY_LIFT_SCALE,
  CARRY_RISE_PX,
  CARRY_SQUASH,
  CARRY_STRAIGHT_LEAN_RATIO,
  CARRY_TEMPO_JITTER,
  CARRY_TRAVEL_BASE_MS,
  CARRY_TRAVEL_MS_PER_SQUARE,
  CARRY_WOBBLE_CHANCE,
  CARRY_WOBBLE_DEGREES,
  CARRY_WOBBLE_MS,
  SQUARE_PX,
} from '../constants';
import { fileOf, movePath, rankOf } from '../engine/squares';

export type Point = { x: number; y: number };

type Range = readonly [number, number];

export type CarryPlan = {
  from: Point;
  to: Point;
  /** Where the path turns, in order. A knight turns once. A straight move has none. */
  corners: Point[];
  liftMs: number;
  travelMs: number;
  landMs: number;
  /** The size of the piece while it is off the board. 1 is its size at rest. */
  liftScale: number;
  /** How far up the page the piece comes, in board pixels. */
  rise: number;
  /** The most the piece leans into the travel, in degrees. The sign is the side it leans to. */
  lean: number;
  /** How far the piece sways to each side while it is held, in degrees. Zero on some carries. */
  wobble: number;
  /** One sway, there and back. */
  wobbleMs: number;
  /** How far the path bends away from the straight line, in board pixels. The sign is the side. Zero on a path that turns. */
  bow: number;
  /** How much the piece flattens when it is set down, as a part of its size. */
  squash: number;
};

export type CarryPose = {
  x: number;
  y: number;
  rotate: number;
  scaleX: number;
  scaleY: number;
};

/**
 * The top left corner of a square, in board pixels. Rank 1 is the bottom row.
 * A `flipped` board has rank 8 at the bottom and the h file at the left.
 */
export const squareOrigin = (square: number, flipped = false): Point => ({
  x: (flipped ? BOARD_SIZE - 1 - fileOf(square) : fileOf(square)) * SQUARE_PX,
  y: (flipped ? rankOf(square) : BOARD_SIZE - 1 - rankOf(square)) * SQUARE_PX,
});

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const lerp = (from: number, to: number, amount: number): number => from + (to - from) * amount;

const within = ([low, high]: Range, random: () => number): number => lerp(low, high, random());

const eitherSign = (random: () => number): number => (random() < 0.5 ? -1 : 1);

const BACK_OVERSHOOT = 1.70158;

/** Goes past 1 and comes back, so the piece comes up with a small spring. */
const easeOutBack = (amount: number): number => {
  const rest = amount - 1;
  return 1 + (BACK_OVERSHOOT + 1) * rest ** 3 + BACK_OVERSHOOT * rest ** 2;
};

const easeInOutCubic = (amount: number): number =>
  amount < 0.5 ? 4 * amount ** 3 : 1 - (-2 * amount + 2) ** 3 / 2;

const easeInQuad = (amount: number): number => amount * amount;

const pathLength = (points: readonly Point[]): number =>
  points.slice(1).reduce((sum, point, index) => sum + distance(points[index] ?? point, point), 0);

const distance = (a: Point, b: Point): number => Math.hypot(b.x - a.x, b.y - a.y);

/** The point a part of the way along a path of straight legs, by length. */
const pointAlong = (points: readonly Point[], amount: number): Point => {
  let left = pathLength(points) * amount;
  for (let index = 1; index < points.length; index += 1) {
    const start = points[index - 1] as Point;
    const end = points[index] as Point;
    const leg = distance(start, end);
    if (left <= leg || index === points.length - 1) {
      const part = leg === 0 ? 1 : clamp01(left / leg);
      return { x: lerp(start.x, end.x, part), y: lerp(start.y, end.y, part) };
    }
    left -= leg;
  }
  return points[0] as Point;
};

export const planCarry = (
  fromSquare: number,
  toSquare: number,
  random: () => number,
  flipped = false,
): CarryPlan => {
  const from = squareOrigin(fromSquare, flipped);
  const to = squareOrigin(toSquare, flipped);
  const corners = movePath(fromSquare, toSquare)
    .slice(1, -1)
    .map((square) => squareOrigin(square, flipped));
  const squares = pathLength([from, ...corners, to]) / SQUARE_PX;
  const tempo = 1 + (random() * 2 - 1) * CARRY_TEMPO_JITTER;
  const sideways = Math.sign(to.x - from.x);
  const leanSize = within(CARRY_LEAN_DEGREES, random);
  const pickedSide = eitherSign(random);
  const bow = eitherSign(random) * within(CARRY_BOW_PX, random);
  return {
    from,
    to,
    corners,
    liftMs: CARRY_LIFT_MS * tempo,
    travelMs: (CARRY_TRAVEL_BASE_MS + CARRY_TRAVEL_MS_PER_SQUARE * squares) * tempo,
    landMs: CARRY_LAND_MS * tempo,
    liftScale: within(CARRY_LIFT_SCALE, random),
    rise: within(CARRY_RISE_PX, random),
    // A move along a file has no side to lean to, so it leans less, to a side picked for this carry.
    lean: sideways !== 0 ? sideways * leanSize : pickedSide * leanSize * CARRY_STRAIGHT_LEAN_RATIO,
    wobble: random() < CARRY_WOBBLE_CHANCE ? within(CARRY_WOBBLE_DEGREES, random) : 0,
    wobbleMs: within(CARRY_WOBBLE_MS, random),
    // A path that turns shows its corner, so it does not bend. Garrett, 2026-10-15: "animate that way".
    bow: corners.length > 0 ? 0 : bow,
    squash: within(CARRY_SQUASH, random),
  };
};

/** The time from the release of the piece to its rest on the new square. */
export const carryMoveMs = (plan: CarryPlan): number => plan.travelMs + plan.landMs;

/**
 * The pose of a carried piece. `heldMs` is the time since the piece came up
 * and does not stop. `movedMs` is the time since the piece left its square,
 * or null while it still hangs over it.
 */
export const carryPose = (plan: CarryPlan, heldMs: number, movedMs: number | null): CarryPose => {
  const lifted = easeOutBack(clamp01(heldMs / plan.liftMs));
  const travel = movedMs === null ? 0 : easeInOutCubic(clamp01(movedMs / plan.travelMs));
  const landing = movedMs === null ? 0 : clamp01((movedMs - plan.travelMs) / plan.landMs);
  const held = lifted * (1 - easeInQuad(clamp01(landing / CARRY_DROP_PORTION)));
  const settle = clamp01((landing - CARRY_DROP_PORTION) / (1 - CARRY_DROP_PORTION));
  const flatten = plan.squash * Math.sin(Math.PI * settle) * (1 - settle);

  const dx = plan.to.x - plan.from.x;
  const dy = plan.to.y - plan.from.y;
  const length = Math.hypot(dx, dy) || 1;
  const bend = plan.bow * Math.sin(Math.PI * travel);
  const sway = plan.wobble * Math.sin((2 * Math.PI * heldMs) / plan.wobbleMs) * held;
  const lean = plan.lean * Math.sin(Math.PI * travel);
  const scale = 1 + (plan.liftScale - 1) * held;

  const place = pointAlong([plan.from, ...plan.corners, plan.to], travel);

  return {
    x: place.x + (-dy / length) * bend,
    y: place.y + (dx / length) * bend - plan.rise * held,
    rotate: sway + lean,
    scaleX: scale * (1 + flatten),
    scaleY: scale * (1 - flatten),
  };
};
